import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { Icon, MessageBox, Spacing, Text, Button } from '@reservamos/elements';
import { Trans, useTranslation } from 'react-i18next';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { quickPurchaseBuyerEditTracker } from 'metrics/user-analytics/purchase';
import PurchaserFields from './PurchaserFields';
import TaxpayerIdInput from './TaxpayerIdInput';

const purchaserSelector = formValueSelector('purchaser');

/**
 * PurchaserForm component.
 * Renders a form section for collecting purchaser information with optional invoice toggle.
 * @param {object} props - showPurchaserForm, initialValues, isExchange, showInvoiceToggle, collapsible.
 * @param {boolean} props.showPurchaserForm - Controls visibility of purchaser form fields.
 * @param {object} props.initialValues - Initial values for the purchaser form.
 * @param {boolean} props.isExchange - Flag indicating if this is an exchange transaction.
 * @param {boolean} props.showInvoiceToggle - Controls visibility of the invoice/taxpayer ID section.
 * @param {boolean} props.collapsible - Shows only phone and email with a MessageBox for identity fields.
 * @param {boolean} props.defaultExpanded - When collapsible, start expanded (e.g. buyer data is incomplete).
 */
const PurchaserForm = ({
  showPurchaserForm,
  initialValues,
  isExchange,
  showInvoiceToggle,
  collapsible,
  defaultExpanded,
}) => {
  const { t } = useTranslation('payment');
  const { SHOW_PURCHASER_FORM_BILLING_TITLE: showBillingTitle } = useWhitelabelFeatures();
  const { brand } = useWhitelabelEnvs();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const purchaserName = useSelector((state) => {
    const firstName = purchaserSelector(state, 'purchaserFirstName');
    const lastName = purchaserSelector(state, 'purchaserLastName');
    return [firstName, lastName].filter(Boolean).join(' ');
  });

  const titleText = showBillingTitle
    ? t('purchaser_info_billing', { context: brand })
    : t('purchaser_info');

  const invoiceSection = showInvoiceToggle && (
    <div className="checkout-header">
      <Text size="XL" weight="bold" className="checkout-header-title">
        {t('need_invoice')}
      </Text>
      <TaxpayerIdInput />
    </div>
  );

  if (collapsible && !isExpanded) {
    return (
      <div className="grid gap-3 mb-12">
        <Text size="XL" weight="bold" className="checkout-header-title">
          {titleText}
        </Text>
        <MessageBox borderColor="warning" borderRadius="L" widthFull>
          <div className="grid gap-4 min-h-[2lh] md:grid-cols-[auto_auto] md:gap-6">
            <Spacing size="XS" alignItems="center">
              <div className="flex items-center gap-2">
                <Icon type="emojiSparkles" size="S" />
                <Text size="S">
                  <Trans>{t('quick_checkout_purchaser_message', { name: purchaserName })}</Trans>
                </Text>
              </div>
            </Spacing>
            <Button
              onClick={() => {
                quickPurchaseBuyerEditTracker();
                setIsExpanded(true);
              }}
              isRounded
              buttonType="button"
            >
              <div className="flex items-center gap-1">
                <Text size="S" weight="semibold" color="accent">
                  {t('edit_data')}
                </Text>
                <Icon type="ChevronDown" size="S" color="grayLight" />
              </div>
            </Button>
          </div>
        </MessageBox>
        <PurchaserFields
          hideIdentityFields
          isExchange={isExchange}
          purchaser={initialValues}
          showPurchaserForm={showPurchaserForm}
        />
      </div>
    );
  }

  return (
    <>
      {invoiceSection}
      <div className="checkout-header">
        <Text size="XL" weight="bold" className="checkout-header-title">
          {titleText}
        </Text>
      </div>
      <PurchaserFields
        isExchange={isExchange}
        purchaser={initialValues}
        showPurchaserForm={showPurchaserForm}
      />
    </>
  );
};

PurchaserForm.propTypes = {
  initialValues: PropTypes.object,
  showPurchaserForm: PropTypes.bool,
  isExchange: PropTypes.bool,
  showInvoiceToggle: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
};

PurchaserForm.defaultProps = {
  defaultExpanded: false,
};

export default PurchaserForm;
